// ═══════════════════════════════════════════════════════════════════════════
// HomeActivityPanel — right-rail content for the Home view (Home Redesign):
// "Opportunities for you" (real counts: guest matches, live sponsor deals)
// and "Recent activity" (the same local activity feed the notifications
// panel reads from — see lib/notifications.js). Each row carries a colored
// icon badge per the design; badge colors cycle through the same four-tone
// palette across both lists.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useEffect, useMemo, useState } from 'react';
import { getTopMatches } from '../../lib/matching';
import { listLiveDeals } from '../../lib/sponsorDeals';
import { rankDealsForHost } from '../../lib/dealMatching';
import { readActivity, seedIfEmpty } from '../../lib/notifications';

const PeopleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="9" cy="8" r="3" /><circle cx="17" cy="9" r="2.6" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0M13.5 20a5 5 0 0 1 8-3.8" />
  </svg>
);
const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="5" width="18" height="16" rx="2" /><line x1="16" y1="3" x2="16" y2="7" /><line x1="8" y1="3" x2="8" y2="7" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const BulbIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M9 18h6" /><path d="M10 21h4" />
    <path d="M12 3a6 6 0 0 0-4 10.5c.6.6 1 1.5 1 2.5h6c0-1 .4-1.9 1-2.5A6 6 0 0 0 12 3z" />
  </svg>
);
const LinkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M10 13a5 5 0 0 0 7.07 0l2-2a5 5 0 0 0-7.07-7.07l-1 1" /><path d="M14 11a5 5 0 0 0-7.07 0l-2 2a5 5 0 0 0 7.07 7.07l1-1" />
  </svg>
);
const TagIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20.6 12.6 12.7 20.5a2 2 0 0 1-2.8 0l-6.4-6.4a2 2 0 0 1 0-2.8L11.4 3.4a2 2 0 0 1 1.4-.6H19a2 2 0 0 1 2 2v6.6a2 2 0 0 1-.4 1.2Z" />
    <circle cx="16" cy="8" r="1.4" fill="currentColor" stroke="none" />
  </svg>
);
const WavesIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
    <path d="M8.5 8.5a5 5 0 0 1 0 7M15.5 8.5a5 5 0 0 1 0 7M5.5 5.5a9 9 0 0 1 0 13M18.5 5.5a9 9 0 0 1 0 13" />
  </svg>
);
const SparkIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3v3M12 18v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M3 12h3M18 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
  </svg>
);
const PinIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 21s-6.5-5.9-6.5-11A6.5 6.5 0 0 1 18.5 10c0 5.1-6.5 11-6.5 11z" /><circle cx="12" cy="10" r="2" />
  </svg>
);

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return 'Just now';
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

// The four-tone badge palette both lists cycle through.
const TONES = ['tone-a', 'tone-b', 'tone-c', 'tone-d'];

export default function HomeActivityPanel({ profile, onNavigate }) {
  const viewerId = profile?.user_id || null;
  const [matchCount, setMatchCount] = useState(null);
  const [dealCount, setDealCount] = useState(null);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    if (!viewerId) return;
    let alive = true;
    getTopMatches(profile, { limit: 50, minScore: 18 }).then((m) => {
      if (alive) setMatchCount(Array.isArray(m) ? m.length : 0);
    }).catch(() => {});
    listLiveDeals({ limit: 40 }).then((deals) => {
      if (!alive) return;
      const host = { ...profile, episodes_created: Number(profile?.episodes_created) || 0 };
      setDealCount(rankDealsForHost(deals, host, { limit: 40 }).length);
    }).catch(() => { if (alive) setDealCount(0); });

    seedIfEmpty(viewerId, profile);
    setActivity(readActivity(viewerId).slice(0, 6));
    return () => { alive = false; };
  }, [viewerId, profile]);

  const opportunities = useMemo(() => ([
    matchCount != null && {
      id: 'matches', icon: <PeopleIcon />,
      count: matchCount > 0 ? matchCount : null,
      label: matchCount > 0 ? 'Guests Epio thinks you should meet' : 'Add your niche to get guest matches',
      onClick: () => onNavigate?.(matchCount > 0 ? 'collabs' : 'settings'),
    },
    dealCount != null && {
      id: 'deals', icon: <CalendarIcon />,
      count: dealCount > 0 ? dealCount : null,
      label: dealCount > 0 ? 'Brands looking for shows like yours' : 'Brand matches will show up here as they open',
      onClick: () => onNavigate?.('partners'),
    },
    { id: 'ideas', icon: <BulbIcon />, count: null, label: 'Episode ideas based on what’s trending', onClick: () => onNavigate?.('explore') },
    { id: 'explore', icon: <LinkIcon />, count: null, label: 'Collaboration opportunities', onClick: () => onNavigate?.('explore') },
  ].filter(Boolean)), [matchCount, dealCount, onNavigate]);

  const activityIcon = (type) => {
    if (type === 'sponsorship_offers') return <TagIcon />;
    if (type === 'follows') return <WavesIcon />;
    if (type === 'guest_requests') return <PinIcon />;
    return <SparkIcon />;
  };

  return (
    <>
      <div className="home-activity-block">
        <div className="home-activity-block-title">Opportunities for you</div>
        <div className="home-activity-list">
          {opportunities.map((o, i) => (
            <button type="button" key={o.id} className="home-activity-row" onClick={o.onClick}>
              <span className={`home-activity-row-badge home-activity-row-badge--${TONES[i % TONES.length]}`} aria-hidden>{o.icon}</span>
              {o.count != null && <span className="home-activity-row-count">{o.count}</span>}
              <span className="home-activity-row-label">{o.label}</span>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="home-activity-row-chev"><polyline points="9 6 15 12 9 18" /></svg>
            </button>
          ))}
        </div>
      </div>

      <div className="home-activity-block">
        <div className="home-activity-block-title">Recent activity</div>
        {activity.length === 0 ? (
          <div className="home-activity-empty">Nothing yet — it'll show up here as you build.</div>
        ) : (
          <div className="home-activity-feed">
            {activity.map((a, i) => (
              <div className="home-activity-feed-row" key={a.id}>
                <span className={`home-activity-row-badge home-activity-row-badge--${TONES[i % TONES.length]}`} aria-hidden>
                  {activityIcon(a.type)}
                </span>
                <div className="home-activity-feed-body">
                  <p>{a.text || a.title}</p>
                  <span>{timeAgo(a.created_at || a.ts)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        {activity.length > 0 && (
          <button type="button" className="home-activity-viewall" onClick={() => onNavigate?.('inbox')}>
            View all activity
          </button>
        )}
      </div>
    </>
  );
}
